import { EventEmitterInterface } from '@propertyfinder/pf-frontend-common/src/module/event/emitter.interface';
import { BrowserStorageInterface } from 'pf-frontend-common/src/module/browser/storage.interface';

import { DataStore } from 'common/module/data/store';
import { StatsTealiumAdapter } from 'common/module/stats/tealium/adapter';
import { StatsTealiumDataLayerEventActionEnum } from 'common/module/stats/tealium/data-layer/event-action.enum';
import { StatsTealiumDataLayerEventCategoryEnum } from 'common/module/stats/tealium/data-layer/event-category.enum';
import { StatsTealiumDataLayerTealiumEventEnum } from 'common/module/stats/tealium/data-layer/tealium-event.enum';
import { StatsTealiumProviderInterface } from 'common/module/stats/tealium/provider.interface';
import { PropertyStoreServiceInterface } from 'common/service/property-store/service.interface';

type PropertiesImpressionsUnSentType = Set<string>;
type PropertiesInQueueType = Map<string, boolean>;

interface ImpressionsSessionStorageInterface {
  data: PropertiesInQueueType;
  date: string;
}

interface State {
  queue: PropertiesInQueueType;
}

const IMPRESSIONS_QUEUE_STORAGE_KEY = 'ImpressionsQueue';
const IMPRESSIONS_QUEUE_TIMEOUT_MILISECONDS = 60000;

const statsTealiumAdapter = new StatsTealiumAdapter();

export class PropertyListStatsTealiumImpressionsStore extends DataStore<State> {
  protected state: State;
  private interval: ReturnType<typeof setInterval>;

  public constructor(
    eventEmitter: EventEmitterInterface,
    private browserStorageSessionService: BrowserStorageInterface,
    private propertyStoreService: PropertyStoreServiceInterface,
    private statsTealium: StatsTealiumProviderInterface
  ) {
    super(eventEmitter);
    this.initialize();
  }

  public initialize(): void {
    this.setState({
      queue: new Map(),
    });

    this.handleInterval();
  }

  public destroy(): void {
    clearInterval(this.interval);
  }

  public updateState(queue: PropertiesInQueueType): void {
    this.setState({ ...this.state, queue });
  }

  public getUnSentImpressions(propertiesQueue: PropertiesInQueueType): PropertiesImpressionsUnSentType {
    const impressionsUnSent: PropertiesImpressionsUnSentType = new Set();

    propertiesQueue.forEach((value, key) => {
      if (!value) {
        impressionsUnSent.add(key);
      }
    });

    return impressionsUnSent;
  }

  public sendImpression(impressionsUnSent: PropertiesImpressionsUnSentType): void {
    const propertyListPayload = statsTealiumAdapter.toPropertyTealiumStats(
      this.propertyStoreService.getAll().filter((property) => impressionsUnSent.has(property.id))
    );

    this.statsTealium.send({
      sendToGa: true,
      tealium_event: StatsTealiumDataLayerTealiumEventEnum.search_impression_card,
      event_category: StatsTealiumDataLayerEventCategoryEnum.ecommerce,
      event_action: StatsTealiumDataLayerEventActionEnum.properties_impression,
      event_label: '',
      ...propertyListPayload,
    });

    const propertiesQueue = this.getState().queue;

    impressionsUnSent.forEach((propertyId) => {
      propertiesQueue.set(propertyId, true);
    });

    // clears the queue after the impressions has been sent
    this.updateState(new Map(propertiesQueue));
  }

  private handleInterval(): void {
    this.interval = setInterval(() => {
      const sessionKey = this.browserStorageSessionService.getData(IMPRESSIONS_QUEUE_STORAGE_KEY);

      if (sessionKey) {
        const sessionData = <ImpressionsSessionStorageInterface>sessionKey;
        const diff = Date.now() - new Date(sessionData.date).getTime();

        if (diff > IMPRESSIONS_QUEUE_TIMEOUT_MILISECONDS) {
          const impressionsUnSent = this.getUnSentImpressions(new Map(Object.entries(sessionData.data)));

          if (impressionsUnSent.size > 0) {
            this.sendImpression(impressionsUnSent);
          }
        }
      }
    }, 10000);
  }
}
